// ADA-4WM — 33-Layer Provenance & Mission-Assurance Architecture.
// Four analytical mirrors mapped across 33 layers.

export const MIRRORS = [
  {
    id: "technical",
    name: "Technical Mirror",
    accent: "cyan",
    summary:
      "Payload identity, hash integrity, input/output relationships, software state, model/system identifiers, metadata, anomaly indicators, and evidence-chain continuity.",
    range: [1, 8],
  },
  {
    id: "regulatory",
    name: "Regulatory Mirror",
    accent: "emerald",
    summary:
      "Mapping tools for NIST AI RMF, zero-trust principles, AI governance, auditability, accountability, and provenance. Alignment / mapping — not certification.",
    range: [9, 16],
  },
  {
    id: "temporal",
    name: "Temporal Mirror",
    accent: "sky",
    summary:
      "Timestamps, event sequence, previous-record relationships, elapsed time, drift across observations, reconstruction chronology, and anchor continuity.",
    range: [17, 24],
  },
  {
    id: "operational",
    name: "Operational Mirror",
    accent: "amber",
    summary:
      "System status, source, destination, mission/event context, confidence, anomalies, environmental state, operator notes, and integrity outcome.",
    range: [25, 33],
  },
];

export const LAYERS = [
  { n: 1, mirror: "technical", name: "Payload Identity", desc: "Assigns a unique identity to each captured payload." },
  { n: 2, mirror: "technical", name: "Input / Output Binding", desc: "Binds input payloads to their produced outputs." },
  { n: 3, mirror: "technical", name: "Hash Integrity Core", desc: "SHA-256 integrity computation via K-Crypto." },
  { n: 4, mirror: "technical", name: "Software State Capture", desc: "Snapshots relevant software / model state." },
  { n: 5, mirror: "technical", name: "Model / System Identifiers", desc: "Records model and system identifier metadata." },
  { n: 6, mirror: "technical", name: "Metadata Extraction", desc: "Extracts structured metadata from payloads." },
  { n: 7, mirror: "technical", name: "Anomaly Indicator Map", desc: "Surfaces deterministic anomaly indicators." },
  { n: 8, mirror: "technical", name: "Evidence-Chain Continuity", desc: "L8 drift trigger — flags chain discontinuity.", special: "L8 DRIFT TRIGGER" },
  { n: 9, mirror: "regulatory", name: "NIST AI RMF Mapping", desc: "Maps evidence fields to NIST AI RMF functions (alignment tool)." },
  { n: 10, mirror: "regulatory", name: "Zero-Trust Alignment", desc: "Maps to zero-trust verification principles." },
  { n: 11, mirror: "regulatory", name: "AI Governance Controls", desc: "Fields for AI governance requirement mapping." },
  { n: 12, mirror: "regulatory", name: "Auditability Ledger", desc: "Append-only local audit surface." },
  { n: 13, mirror: "regulatory", name: "Accountability Trace", desc: "Traces operator and origin accountability." },
  { n: 14, mirror: "regulatory", name: "Provenance Assertion", desc: "Asserts provenance of the evidence record." },
  { n: 15, mirror: "regulatory", name: "Policy Conformance Map", desc: "Maps records to policy conformance fields." },
  { n: 16, mirror: "regulatory", name: "Control Attestation Fields", desc: "Structured attestation fields (self-asserted)." },
  { n: 17, mirror: "temporal", name: "Timestamp Authority (Local)", desc: "Local timestamp generation — not an external TSA." },
  { n: 18, mirror: "temporal", name: "Event Sequence Index", desc: "Orders events within the chain." },
  { n: 19, mirror: "temporal", name: "Previous-Record Linkage", desc: "Links each record to the prior record hash." },
  { n: 20, mirror: "temporal", name: "Elapsed-Time Delta", desc: "Computes elapsed time between observations." },
  { n: 21, mirror: "temporal", name: "Drift-Across-Observations", desc: "Tracks drift between sequential observations." },
  { n: 22, mirror: "temporal", name: "Reconstruction Chronology", desc: "Orders states for replay / reconstruction." },
  { n: 23, mirror: "temporal", name: "Anchor Continuity", desc: "Verifies unbroken anchor sequence." },
  { n: 24, mirror: "temporal", name: "Temporal Anchor Match", desc: "L24 anchor match — confirms temporal anchor alignment.", special: "L24 ANCHOR MATCH" },
  { n: 25, mirror: "operational", name: "System Status Monitor", desc: "Reports overall system operating status." },
  { n: 26, mirror: "operational", name: "Source Attribution", desc: "Captures the source of the event." },
  { n: 27, mirror: "operational", name: "Destination Routing", desc: "Captures the destination / consumer." },
  { n: 28, mirror: "operational", name: "Mission / Event Context", desc: "Records mission or event context." },
  { n: 29, mirror: "operational", name: "Confidence Scoring", desc: "Holds confidence metrics for the observation." },
  { n: 30, mirror: "operational", name: "Environmental State", desc: "Captures environmental conditions." },
  { n: 31, mirror: "operational", name: "Operator Notes", desc: "Free-text operator notes." },
  { n: 32, mirror: "operational", name: "Integrity Outcome", desc: "Records the final integrity outcome." },
  { n: 33, mirror: "operational", name: "Reconstruction Availability", desc: "Indicates whether replay/reconstruction is available." },
];

export const STATUS_STYLES = {
  READY: { bg: "bg-slate-800", text: "text-slate-300", border: "border-slate-600", icon: "Circle" },
  OBSERVED: { bg: "bg-sky-950", text: "text-sky-300", border: "border-sky-600", icon: "Eye" },
  ANCHORED: { bg: "bg-emerald-950", text: "text-emerald-300", border: "border-emerald-600", icon: "Anchor" },
  WARNING: { bg: "bg-amber-950", text: "text-amber-300", border: "border-amber-600", icon: "AlertTriangle" },
  DRIFT: { bg: "bg-rose-950", text: "text-rose-300", border: "border-rose-600", icon: "Waves" },
  VERIFIED: { bg: "bg-cyan-950", text: "text-cyan-300", border: "border-cyan-500", icon: "ShieldCheck" },
};

const MIRROR_BASE = { technical: "ANCHORED", regulatory: "OBSERVED", temporal: "VERIFIED", operational: "OBSERVED" };

function baseStatus(mirror, hasRecords) {
  return hasRecords ? MIRROR_BASE[mirror] || "READY" : "READY";
}

// Special-case indicator layers (e.g. L8 drift trigger, L24 anchor match).
function specialStatus(n, { total, failures, driftAlerts }) {
  switch (n) {
    case 3:
      return total > 0 ? "VERIFIED" : null;
    case 8:
      if (failures > 0) return "WARNING";
      if (driftAlerts > 0) return "DRIFT";
      return total > 0 ? "ANCHORED" : "READY";
    case 21:
      return driftAlerts > 0 ? "WARNING" : null;
    case 24:
      return total > 0 ? "VERIFIED" : null;
    case 32:
      if (failures > 0) return "WARNING";
      return total > 0 ? "VERIFIED" : "READY";
    default:
      return null;
  }
}

// Derive layer statuses from current chain state (deterministic, honest).
export function computeLayerStatuses(stats) {
  const hasRecords = stats.total > 0;
  return LAYERS.map((l) => ({
    ...l,
    status: specialStatus(l.n, stats) ?? baseStatus(l.mirror, hasRecords),
  }));
}
